/**
 * Two-bone analytic IK core — pure math (no three.js), the control-rig building block that the
 * pose controller applies to arm/leg chains (e.g. hands onto ledge points in the parkour pass).
 *
 * Chain-plane solution by law of cosines. Angles are returned for the chain plane:
 *  - rootAngle: rotation at the root joint away from the straight root→target line
 *  - midAngle:  interior bend at the mid joint (π = straight)
 * The caller orients the plane (pole vector) and converts to quaternions.
 */
export interface TwoBoneSolution {
  rootAngle: number;
  midAngle: number;
  /** Target distance actually solved for, after reach clamping. */
  clampedDist: number;
  reachable: boolean;
}

export function solveTwoBone(lenA: number, lenB: number, targetDist: number): TwoBoneSolution {
  const maxReach = lenA + lenB;
  const minReach = Math.abs(lenA - lenB);
  const eps = 1e-6;
  const d = Math.min(Math.max(targetDist, minReach + eps), maxReach - eps);
  const reachable = targetDist >= minReach && targetDist <= maxReach;

  // Law of cosines. midAngle is the interior angle at the elbow/knee.
  const cosMid = (lenA * lenA + lenB * lenB - d * d) / (2 * lenA * lenB);
  const midAngle = Math.acos(Math.min(Math.max(cosMid, -1), 1));
  const cosRoot = (lenA * lenA + d * d - lenB * lenB) / (2 * lenA * d);
  const rootAngle = Math.acos(Math.min(Math.max(cosRoot, -1), 1));

  return { rootAngle, midAngle, clampedDist: d, reachable };
}

/** Forward-kinematics check used by tests: end-effector distance for given plane angles. */
export function forwardDist(lenA: number, lenB: number, midAngle: number): number {
  // Interior angle midAngle between the two bones → straight-line distance root→end.
  return Math.sqrt(lenA * lenA + lenB * lenB - 2 * lenA * lenB * Math.cos(midAngle));
}
